/*
 * Hound-Tees help centre.
 *
 * Markup: snippets/ht-help-search.liquid (the search box) and sections/ht-help-*.liquid. Answers are
 * articles in the "help" blog. templates/blog.ht-help-index.liquid serves them as a small JSON index
 * (?view=ht-help-index), which this script searches in the browser: typo tolerant, synonym aware
 * (Content > Metaobjects > Help Centre settings) and ranked, so a question typed the way customers
 * write it still finds the answer. Pressing Enter or "See all results" goes to Shopify's own search
 * (/search?type=article&view=ht-help), so committed searches also land in Shopify's search reports.
 *
 * Analytics: ht_help_* events through Shopify.analytics.publish (customer events). Queries are
 * normalised and redacted (emails, phone and order numbers) before they leave the page.
 */
(function () {
  'use strict';

  // Every help section includes this file, so the second copy on a page stops here.
  if (typeof window !== 'undefined') {
    if (window.HtHelpCore) return;
  }

  /* Core: pure functions, also loaded by qa/ tests in Node ---------------------------------- */

  const STOP = new Set(
    ('a an the and or but if of to in on at for from by with about as is are was were be been am ' +
      'i me my mine we our us you your it its this that these those do does did can could would ' +
      'should will shall may might must have has had how what when where which who why there here ' +
      'please hi hello hey thanks thank just so get got im ive id dont cant wont isnt not no yes ' +
      'want need like know help any some much many very really also too still')
      .split(' ')
  );

  // UK and US spellings fold to one form on both sides.
  const FOLD = {
    colour: 'color', colours: 'colors', personalise: 'personalize', personalised: 'personalized',
    personalisation: 'personalization', customise: 'customize', customised: 'customized',
    organise: 'organize', cancelled: 'canceled', cancelling: 'canceling', pyjamas: 'pajamas',
    jewellery: 'jewelry', catalogue: 'catalog', centre: 'center', favourite: 'favorite',
    tshirt: 'tee', tshirts: 'tee', teeshirt: 'tee', houndtee: 'hound', houndtees: 'hound',
    instalment: 'installment', instalments: 'installments', enrol: 'enroll', grey: 'grey',
  };

  // t title, k tags, c topic, x short answer, b answer, q search words learned from real customer
  // questions (Help: search words). Weights tuned on half the questions, tested on the other half.
  const FIELD_WEIGHT = { t: 6, k: 5, c: 3, x: 3, b: 0.5, q: 5 };
  const FIELDS = ['t', 'k', 'c', 'x', 'b', 'q'];

  function normalize(text) {
    return String(text == null ? '' : text)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[‘’‛′'`]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function stem(token) {
    let t = token;
    if (t.length <= 3 || /^\d+$/.test(t)) return t;
    if (t.endsWith('ies') && t.length > 4) return t.slice(0, -3) + 'y';
    if (/(ss|sh|ch|x|z)es$/.test(t)) return t.slice(0, -2);
    if (t.endsWith('ing') && t.length > 5) t = t.slice(0, -3);
    else if (t.endsWith('ed') && t.length > 4) t = t.slice(0, -2);
    else if (t.endsWith('s') && !/(ss|us|is)$/.test(t)) return t.slice(0, -1);
    return t.replace(/([bdgmnpt])\1$/, '$1');
  }

  function term(word) {
    return stem(FOLD[word] || word);
  }

  function words(text) {
    const norm = normalize(text);
    return norm ? norm.split(' ') : [];
  }

  function terms(text) {
    return words(text).map(term);
  }

  function parseSynonyms(text) {
    return String(text || '')
      .split(/\r?\n/)
      .map((line) =>
        line
          .split(',')
          .map((member) => terms(member))
          .filter((member) => member.length)
      )
      .filter((group) => group.length > 1);
  }

  function containsSequence(sequence, part) {
    if (!part.length || part.length > sequence.length) return -1;
    outer: for (let i = 0; i <= sequence.length - part.length; i++) {
      for (let j = 0; j < part.length; j++) {
        if (sequence[i + j] !== part[j]) continue outer;
      }
      return i;
    }
    return -1;
  }

  function prepare(raw, synonymsText) {
    const groups = parseSynonyms(synonymsText);
    const totals = { t: 0, k: 0, c: 0, x: 0, b: 0, q: 0 };
    const docs = (raw && raw.articles ? raw.articles : []).map((a) => {
      // Tags are customer phrasings: kept whole for phrase matches as well as word by word.
      const tagPhrases = (a.k || []).map((tag) => terms(tag)).filter((p) => p.length);
      const seq = {
        t: terms(a.t),
        k: [].concat.apply([], tagPhrases),
        c: terms(a.c),
        x: terms(a.x),
        b: terms(a.b),
        q: terms(a.q),
      };
      const tf = {};
      const len = {};
      FIELDS.forEach((f) => {
        const counts = new Map();
        seq[f].forEach((t) => counts.set(t, (counts.get(t) || 0) + 1));
        groups.forEach((group, gi) => {
          let n = 0;
          group.forEach((member) => {
            if (member.length === 1) n += counts.get(member[0]) || 0;
            else if (containsSequence(seq[f], member) > -1) n += 1;
          });
          if (n) counts.set('@' + gi, n);
        });
        tf[f] = counts;
        len[f] = Math.max(1, seq[f].length);
        totals[f] += len[f];
      });
      const phrases = tagPhrases.filter((p) => p.length >= 2);
      for (let i = 0; i < seq.t.length - 1; i++) phrases.push([seq.t[i], seq.t[i + 1]]);
      return {
        a: a,
        tf: tf,
        len: len,
        phrases: phrases,
        title: ' ' + normalize(a.t) + ' ',
        pos: typeof a.p === 'number' ? a.p : parseInt(a.p, 10) || 999,
      };
    });
    const n = docs.length || 1;
    const avg = {};
    FIELDS.forEach((f) => {
      avg[f] = totals[f] / n || 1;
    });
    const df = new Map();
    docs.forEach((d) => {
      const seen = new Set();
      FIELDS.forEach((f) => d.tf[f].forEach((count, t) => seen.add(t)));
      seen.forEach((t) => df.set(t, (df.get(t) || 0) + 1));
    });
    const vocab = [];
    df.forEach((count, t) => {
      if (t.charAt(0) !== '@') vocab.push(t);
    });
    return { docs: docs, df: df, vocab: vocab, groups: groups, n: n, avg: avg };
  }

  // Damerau-Levenshtein (optimal string alignment) with an early exit above max.
  function distance(a, b, max) {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    const rows = a.length + 1;
    const cols = b.length + 1;
    let prev2 = null;
    let prev = new Array(cols);
    for (let j = 0; j < cols; j++) prev[j] = j;
    for (let i = 1; i < rows; i++) {
      const cur = new Array(cols);
      cur[0] = i;
      let rowMin = cur[0];
      for (let j = 1; j < cols; j++) {
        const cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        let v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        if (prev2 && i > 1 && j > 1 && a.charAt(i - 1) === b.charAt(j - 2) && a.charAt(i - 2) === b.charAt(j - 1)) {
          v = Math.min(v, prev2[j - 2] + 1);
        }
        cur[j] = v;
        if (v < rowMin) rowMin = v;
      }
      if (rowMin > max) return max + 1;
      prev2 = prev;
      prev = cur;
    }
    return prev[cols - 1];
  }

  function idf(index, t) {
    const count = index.df.get(t);
    if (!count) return 0;
    return Math.min(4, Math.max(0.5, Math.log(1 + index.n / count)));
  }

  function analyse(index, query) {
    const raw = words(query).slice(0, 40);
    const qt = raw.map((w, i) => ({
      w: w,
      t: term(w),
      stop: STOP.has(w),
      last: i === raw.length - 1,
      groups: new Set(),
      exp: new Map(),
      weight: 1,
    }));
    const seq = qt.map((q) => q.t);
    index.groups.forEach((group, gi) => {
      group.forEach((member) => {
        if (member.length === 1) {
          qt.forEach((q) => {
            if (q.t === member[0]) q.groups.add(gi);
          });
        } else {
          const at = containsSequence(seq, member);
          if (at > -1) for (let k = at; k < at + member.length; k++) qt[k].groups.add(gi);
        }
      });
    });
    qt.forEach((q) => {
      const exp = q.exp;
      exp.set(q.t, 1);
      const known = index.df.has(q.t);
      if (!q.stop && (q.last ? q.t.length >= 2 : q.t.length >= 4)) {
        index.vocab.forEach((v) => {
          if (v !== q.t && v.indexOf(q.t) === 0) {
            const quality = 0.72 + 0.16 * (q.t.length / v.length);
            if ((exp.get(v) || 0) < quality) exp.set(v, quality);
          }
        });
      }
      if (!q.stop && !known && q.t.length >= 4 && !/^\d+$/.test(q.t)) {
        const max = q.t.length >= 8 ? 2 : 1;
        index.vocab.forEach((v) => {
          if (v.charAt(0) !== q.t.charAt(0) || exp.has(v)) return;
          if (distance(q.t, v, max) <= max) exp.set(v, 0.68);
        });
      }
      q.groups.forEach((gi) => {
        if (!exp.has('@' + gi)) exp.set('@' + gi, 0.8);
      });
      if (q.stop) {
        q.weight = 0.15;
      } else {
        let best = idf(index, q.t);
        exp.forEach((quality, t) => {
          best = Math.max(best, idf(index, t) * quality);
        });
        q.weight = best || 1;
      }
    });
    return { norm: raw.join(' '), qt: qt, seq: seq };
  }

  // Ranking: BM25-style per field (repeats saturate, long fields count for less), weighted by
  // field, plus a bonus when a whole tag phrase or a title word pair appears in the question.
  const TUNE = { k1: 2, b: { t: 0.3, k: 0.4, c: 0, x: 0.5, b: 0.75, q: 0.3 }, phrase: 5 };

  function fieldScore(index, d, f, t) {
    const count = d.tf[f].get(t);
    if (!count) return 0;
    const norm = 1 - TUNE.b[f] + TUNE.b[f] * (d.len[f] / index.avg[f]);
    return (FIELD_WEIGHT[f] * count * (TUNE.k1 + 1)) / (count + TUNE.k1 * norm);
  }

  function search(index, query, options) {
    const opts = options || {};
    const limit = opts.limit || 6;
    if (!index || !index.docs.length) return [];
    const parsed = analyse(index, query);
    const qt = parsed.qt;
    const content = qt.filter((q) => !q.stop);
    if (!content.length) return [];
    const long = opts.mode === 'long' || content.length > 6;
    const results = [];
    index.docs.forEach((d) => {
      let score = 0;
      let got = 0;
      let total = 0;
      let contentHit = false;
      const hits = new Set();
      qt.forEach((q) => {
        total += q.weight;
        let best = 0;
        let bestTerm = null;
        q.exp.forEach((quality, t) => {
          let s = 0;
          FIELDS.forEach((f) => {
            s += fieldScore(index, d, f, t);
          });
          s *= quality;
          if (s > best) {
            best = s;
            bestTerm = t;
          }
        });
        if (best > 0) {
          got += q.weight;
          score += q.weight * best;
          if (!q.stop) contentHit = true;
          if (bestTerm) hits.add(bestTerm);
          q.exp.forEach((quality, t) => {
            if (t.charAt(0) === '@' && FIELDS.some((f) => d.tf[f].has(t))) hits.add(t);
          });
        }
      });
      if (!contentHit || score <= 0) return;
      d.phrases.forEach((p) => {
        if (containsSequence(parsed.seq, p) > -1) {
          let weight = 0;
          p.forEach((t) => {
            if (!STOP.has(t)) weight += idf(index, t) || 1;
          });
          score += TUNE.phrase * weight;
        }
      });
      const coverage = total ? got / total : 0;
      if (!long) score *= 0.35 + 0.65 * coverage * coverage;
      if (qt.length >= 2 && d.title.indexOf(' ' + parsed.norm + ' ') > -1) {
        score *= 1.6;
      } else {
        for (let i = 0; i < qt.length - 1; i++) {
          if (!qt[i].stop && !qt[i + 1].stop && d.title.indexOf(' ' + qt[i].w + ' ' + qt[i + 1].w + ' ') > -1) {
            score *= 1.15;
            break;
          }
        }
      }
      score += (1000 - d.pos) / 1e6;
      results.push({ doc: d.a, score: score, hits: hits });
    });
    results.sort((x, y) => y.score - x.score);
    if (!results.length) return results;
    const floor = Math.max(opts.min || 0.9, results[0].score * (long ? 0.3 : 0.12));
    return results.filter((r) => r.score >= floor).slice(0, limit);
  }

  function escapeHtml(text) {
    return String(text == null ? '' : text).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    })[c]);
  }

  function highlight(index, text, hits) {
    const source = String(text == null ? '' : text);
    if (!hits || !hits.size) return escapeHtml(source);
    const groupTerms = new Set();
    hits.forEach((t) => {
      if (t.charAt(0) === '@') {
        const group = index.groups[parseInt(t.slice(1), 10)] || [];
        group.forEach((member) => member.forEach((m) => groupTerms.add(m)));
      }
    });
    let marks = 0;
    return source
      .split(/([A-Za-z0-9À-ɏ'’]+)/)
      .map((part, i) => {
        if (i % 2 === 0) return escapeHtml(part);
        const w = normalize(part);
        if (!w || STOP.has(w) || marks >= 3) return escapeHtml(part);
        const t = term(w);
        if (hits.has(t) || groupTerms.has(t)) {
          marks += 1;
          return '<mark>' + escapeHtml(part) + '</mark>';
        }
        return escapeHtml(part);
      })
      .join('');
  }

  // Privacy: anything that could identify someone is replaced before a query is stored, tracked or
  // sent to /search. Over-redacting is fine (a 4-digit postcode becomes [number]); under-redacting
  // is not. intent() reads redact()'s markers so the two can't disagree.
  const STREET =
    'st|street|rd|road|ave|avenue|dr|drive|ct|court|pl|place|cres|crescent|pde|parade|tce|terrace|' +
    'hwy|highway|ln|lane|way|blvd|boulevard|cl|close|cct|circuit|gr|grove|sq|square';

  function redact(query) {
    return String(query || '')
      .replace(/\S*@\S*/g, ' [email] ')
      .replace(/\b(my name is|name\s*:)\s+.*$/i, '$1 [name]')
      .replace(new RegExp('\\b\\d+[a-z]?(\\s*/\\s*\\d+)?\\s+([a-z\'-]+\\s+){1,3}(' + STREET + ')\\b.*$', 'i'), ' [address] ')
      .replace(/\+?\d[\d\s().-]{6,}\d/g, (m) => (m.replace(/\D/g, '').length >= 8 ? ' [phone] ' : m))
      .replace(/(^|[^0-9])#\d{3,}(?![0-9])/g, '$1 [order] ')
      .replace(/(^|[^0-9])\d{5,6}(?![0-9])/g, '$1 [order] ')
      .replace(/\d{4,}/g, ' [number] ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase()
      .slice(0, 100);
  }

  // 'order' needs an explicit cue (#66300, "order 66300" or a 5-6 digit number): Hound-Tees order
  // numbers are 5 digits, while a 4-digit number is more likely a postcode or a year.
  function intent(query) {
    const q = String(query || '');
    const r = redact(q);
    if (r.indexOf('[email]') > -1) return 'email';
    if (r.indexOf('[phone]') > -1) return 'phone';
    if (r.indexOf('[address]') > -1 || r.indexOf('[name]') > -1) return 'personal';
    if (r.indexOf('[order]') > -1 || /\border\s*(no\.?|number|#)?\s*\d{3,}/i.test(q)) return 'order';
    return null;
  }

  const Core = {
    TUNE: TUNE,
    FIELD_WEIGHT: FIELD_WEIGHT,
    normalize: normalize,
    terms: terms,
    prepare: prepare,
    search: search,
    highlight: highlight,
    intent: intent,
    redact: redact,
    escapeHtml: escapeHtml,
    distance: distance,
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = Core;
    return;
  }
  window.HtHelpCore = Core;
  if (!window.customElements || !window.fetch) return;

  /* Browser ------------------------------------------------------------------------------------ */

  const SETTLE_MS = 1500;
  const ANNOUNCE_MS = 600;
  const LAST_QUERY_KEY = 'hthc:lastq';
  const sentQueries = new Set();
  const indexes = new Map();

  function readJSON(node) {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  function storage() {
    try {
      return window.sessionStorage;
    } catch (error) {
      return null;
    }
  }

  function remember(key, value) {
    const store = storage();
    if (!store) return;
    try {
      store.setItem(key, value);
    } catch (error) {
      // Private mode or full storage: nothing to keep.
    }
  }

  function recall(key) {
    const store = storage();
    if (!store) return null;
    try {
      return store.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function analyticsAllowed() {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy) return false;
    try {
      if (typeof privacy.analyticsProcessingAllowed === 'function') return privacy.analyticsProcessingAllowed();
      if (typeof privacy.userCanBeTracked === 'function') return privacy.userCanBeTracked();
    } catch (error) {
      return false;
    }
    return false;
  }

  // Littledata's GA4 tag (property "Hound-Tees - GA4 Main Account"). The page also loads other
  // Google tags, so help events go to this one only. window.HtHelpGa4Id overrides it.
  const GA4_ID = 'G-QE67YRCDTM';

  function track(name, detail) {
    if (window.Shopify && window.Shopify.designMode) return;
    try {
      const analytics = window.Shopify && window.Shopify.analytics;
      if (analytics && typeof analytics.publish === 'function') analytics.publish(name, detail);
    } catch (error) {
      // Analytics must never break the help centre.
    }
    try {
      // GA4 gets a copy only when its tag is on the page and the visitor allows analytics.
      // Set window.HtHelpNoGa4 = true (e.g. in a Custom Liquid block) to stop it.
      const target = window.HtHelpGa4Id || GA4_ID;
      if (window.HtHelpNoGa4 || typeof window.gtag !== 'function' || !analyticsAllowed()) return;
      if (!window.google_tag_manager || !window.google_tag_manager[target]) return;
      const params = { send_to: target };
      Object.keys(detail || {}).forEach((key) => {
        const value = detail[key];
        const clean = typeof value === 'string' ? value.slice(0, 100) : value;
        if (key === 'q') params.search_term = clean;
        else params['help_' + key] = clean;
      });
      window.gtag('event', name, params);
    } catch (error) {
      // Same as above.
    }
  }

  function trackSearch(source, query, count, top, engine) {
    const q = redact(query);
    if (q.length < 3) return;
    const key = source + '|' + q;
    if (sentQueries.has(key)) return;
    sentQueries.add(key);
    remember(LAST_QUERY_KEY, q);
    const detail = { q: q, results: count, top: top || '', source: source, engine: engine || 'index' };
    track('ht_help_search', detail);
    if (!count) track('ht_help_no_results', detail);
  }

  async function fetchIndex(url) {
    const store = storage();
    const cacheKey = 'hthc:index:' + url;
    if (store) {
      try {
        const cached = store.getItem(cacheKey);
        if (cached) return JSON.parse(cached);
      } catch (error) {
        // Ignore a broken cache entry and fetch again.
      }
    }
    let next = url;
    let guard = 0;
    const articles = [];
    while (next && guard < 8) {
      guard += 1;
      const response = await fetch(next, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Help index ' + response.status);
      const data = JSON.parse(await response.text());
      (data.articles || []).forEach((a) => articles.push(a));
      next = data.next || null;
    }
    const raw = { articles: articles };
    if (store) {
      try {
        store.setItem(cacheKey, JSON.stringify(raw));
      } catch (error) {
        // Storage full: the page still works, it just fetches again next time.
      }
    }
    return raw;
  }

  function loadIndex(url, synonyms) {
    const key = url + '|' + synonyms;
    if (!indexes.has(key)) {
      indexes.set(
        key,
        fetchIndex(url)
          .then((raw) => prepare(raw, synonyms))
          .catch((error) => {
            // Forget the failure so the next focus or keystroke tries again.
            indexes.delete(key);
            throw error;
          })
      );
    }
    return indexes.get(key);
  }

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function searchUrl(base, query) {
    return (
      base +
      '?q=' + encodeURIComponent(query) +
      '&type=article&options%5Bprefix%5D=last&view=ht-help'
    );
  }

  class HtHelpSearch extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.config = readJSON(this.querySelector('script[data-hthc-config]')) || {};
      this.source = this.dataset.source || 'help';
      this.form = this.querySelector('form');
      this.input = this.querySelector('.hthc-search__input');
      this.popup = this.querySelector('.hthc-search__popup');
      this.list = this.querySelector('.hthc-search__list');
      this.note = this.querySelector('.hthc-search__note');
      this.status = this.querySelector('[data-hthc-status]');
      this.clearButton = this.querySelector('.hthc-search__clear');
      if (!this.form || !this.input || !this.list) return;

      this.options = [];
      this.active = -1;
      this.results = [];
      this.query = '';

      const input = this.input;
      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-expanded', 'false');
      input.setAttribute('aria-controls', this.list.id);
      input.setAttribute('aria-haspopup', 'listbox');

      input.addEventListener('focus', () => {
        this.ensureIndex();
        if (!input.value.trim()) this.renderPopular();
      });
      input.addEventListener('input', () => this.onInput());
      input.addEventListener('keydown', (event) => this.onKeydown(event));
      input.addEventListener('blur', () => {
        window.setTimeout(() => {
          if (!this.contains(document.activeElement)) this.close();
        }, 120);
      });
      this.list.addEventListener('mousedown', (event) => event.preventDefault());
      this.list.addEventListener('click', (event) => {
        const option = event.target.closest('[role="option"]');
        if (option) this.choose(this.options.indexOf(option));
      });
      this.list.addEventListener('mousemove', (event) => {
        const option = event.target.closest('[role="option"]');
        if (option) this.setActive(this.options.indexOf(option), false);
      });
      if (this.clearButton) {
        this.clearButton.addEventListener('click', () => {
          input.value = '';
          this.query = '';
          this.toggleClear();
          this.close();
          input.focus();
        });
      }
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.close();
      });
      this.toggleClear();
      if (this.dataset.preload === 'idle') {
        const start = () => this.ensureIndex();
        if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 3000 });
        else window.setTimeout(start, 1500);
      }
    }

    ensureIndex() {
      if (!this.indexPromise) {
        this.indexPromise = loadIndex(this.dataset.indexUrl, this.config.synonyms || '').catch(() => {
          this.indexPromise = null;
          return null;
        });
      }
      return this.indexPromise;
    }

    toggleClear() {
      if (this.clearButton) this.clearButton.hidden = !this.input.value;
    }

    onInput() {
      this.toggleClear();
      const query = this.input.value.trim();
      this.query = query;
      // Every change invalidates answers still on their way for an older query.
      const ticket = (this.ticket = (this.ticket || 0) + 1);
      if (this.abort) this.abort.abort();
      window.clearTimeout(this.settleTimer);
      window.clearTimeout(this.announceTimer);
      if (query.length < 2) {
        this.results = [];
        if (!query) this.renderPopular();
        else this.close();
        return;
      }
      this.ensureIndex().then((index) => {
        if (ticket !== this.ticket) return;
        if (index) {
          this.results = search(index, query, { limit: window.innerWidth < 750 ? 5 : 6 });
          this.render(index, query);
          this.scheduleSettle(query, 'index');
        } else if (intent(query)) {
          // Never send an order number, email or address to Shopify's search.
          this.results = [];
          this.render(null, query);
        } else {
          this.nativeSuggest(query, ticket);
        }
      });
    }

    scheduleSettle(query, engine) {
      window.clearTimeout(this.settleTimer);
      this.settleTimer = window.setTimeout(() => {
        const top = this.results[0] ? this.results[0].doc.h : '';
        trackSearch(this.source, query, this.results.length, top, engine);
      }, SETTLE_MS);
    }

    async nativeSuggest(query, ticket) {
      try {
        if (this.abort) this.abort.abort();
        this.abort = 'AbortController' in window ? new AbortController() : null;
        const url =
          (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') +
          'search/suggest.json?q=' + encodeURIComponent(query) +
          '&resources%5Btype%5D=article&resources%5Blimit%5D=10&resources%5Blimit_scope%5D=each' +
          '&resources%5Boptions%5D%5Bfields%5D=title,body,tag';
        const response = await fetch(url, this.abort ? { signal: this.abort.signal } : {});
        const data = await response.json();
        if (ticket !== this.ticket) return;
        const blogPath = this.dataset.blogPath || '/blogs/help/';
        const articles = ((data.resources && data.resources.results && data.resources.results.articles) || [])
          .filter((a) => a.url && a.url.indexOf(blogPath) > -1)
          .slice(0, 6);
        this.results = articles.map((a) => ({
          doc: { t: a.title, u: a.url, h: a.handle, c: '', x: '' },
          hits: new Set(),
        }));
        this.render(null, query);
        this.scheduleSettle(query, 'native');
      } catch (error) {
        // Aborted or offline: leave the last results.
      }
    }

    clearOptions() {
      this.list.textContent = '';
      this.options = [];
      this.active = -1;
      this.input.removeAttribute('aria-activedescendant');
    }

    addOption(href, html, kind, handle) {
      const id = this.list.id + '-opt-' + this.options.length;
      const li = element('li', 'hthc-search__option hthc-search__option--' + kind);
      li.id = id;
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', 'false');
      li.dataset.href = href;
      if (handle) li.dataset.handle = handle;
      li.innerHTML = html;
      this.list.appendChild(li);
      this.options.push(li);
      return li;
    }

    renderPopular() {
      const popular = this.config.popular || [];
      if (!popular.length) {
        this.close();
        return;
      }
      this.clearOptions();
      this.setNote(this.config.popularLabel || 'Popular questions');
      popular.forEach((item) => {
        this.addOption(item.u, '<span class="hthc-search__q">' + escapeHtml(item.t) + '</span>', 'popular', item.h);
      });
      this.open();
    }

    render(index, query) {
      this.clearOptions();
      const kind = intent(query);
      const results = this.results.slice();
      if (kind && this.config.orderHelp && this.config.orderHelp.length) {
        const order = this.config.orderHelp;
        results.sort((a, b) => {
          const ia = order.indexOf(a.doc.h);
          const ib = order.indexOf(b.doc.h);
          return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
        });
      }
      if (!results.length) {
        this.setNote(this.config.noResults || "We haven't written that one yet. Try other words, or ask the crew.");
        if (this.config.contactUrl) {
          this.addOption(
            this.config.contactUrl,
            '<span class="hthc-search__q">' + escapeHtml(this.config.contactLabel || 'Ask the crew') + '</span>',
            'contact'
          );
        }
      } else {
        this.setNote(kind ? this.config.orderNote || 'Looking for an order? These should help.' : '');
        results.forEach((r, i) => {
          const d = r.doc;
          let html =
            '<span class="hthc-search__q">' + (index ? highlight(index, d.t, r.hits) : escapeHtml(d.t)) + '</span>';
          if (d.c) html += '<span class="hthc-search__cat">' + escapeHtml(d.c) + '</span>';
          if (i === 0 && d.x) {
            const short = d.x.length > 140 ? d.x.slice(0, 137).replace(/\s+\S*$/, '') + '...' : d.x;
            html += '<span class="hthc-search__x">' + escapeHtml(short) + '</span>';
          }
          this.addOption(d.u, html, 'answer', d.h);
        });
      }
      if (!kind && this.dataset.searchUrl) {
        this.addOption(
          searchUrl(this.dataset.searchUrl, query),
          '<span class="hthc-search__q">' +
            escapeHtml((this.config.seeAll || 'See all results for "[q]"').replace('[q]', query)) +
            '</span>',
          'all'
        );
      }
      this.open();
      this.announceTimer = window.setTimeout(() => {
        if (!this.status) return;
        const count = results.length;
        this.status.textContent = count
          ? count + (count === 1 ? ' answer found.' : ' answers found.') + ' Use the up and down arrows to choose.'
          : 'No answers found.';
      }, ANNOUNCE_MS);
    }

    setNote(text) {
      if (!this.note) return;
      this.note.textContent = text || '';
      this.note.hidden = !text;
    }

    open() {
      if (!this.options.length) {
        this.close();
        return;
      }
      this.popup.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      // Results still in flight belong to a list that is no longer showing.
      this.ticket = (this.ticket || 0) + 1;
      window.clearTimeout(this.announceTimer);
      if (this.popup) this.popup.hidden = true;
      if (this.input) {
        this.input.setAttribute('aria-expanded', 'false');
        this.input.removeAttribute('aria-activedescendant');
      }
      this.active = -1;
      this.options.forEach((o) => o.setAttribute('aria-selected', 'false'));
    }

    setActive(i, scroll) {
      this.options.forEach((o, n) => o.setAttribute('aria-selected', n === i ? 'true' : 'false'));
      this.active = i;
      if (i < 0) {
        this.input.removeAttribute('aria-activedescendant');
        return;
      }
      const option = this.options[i];
      this.input.setAttribute('aria-activedescendant', option.id);
      if (scroll !== false) option.scrollIntoView({ block: 'nearest' });
    }

    onKeydown(event) {
      const isOpen = !this.popup.hidden;
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          if (!isOpen) {
            if (this.input.value.trim().length >= 2) this.onInput();
            else this.renderPopular();
            return;
          }
          this.setActive(this.active + 1 >= this.options.length ? 0 : this.active + 1);
          break;
        case 'ArrowUp':
          if (!isOpen) return;
          event.preventDefault();
          this.setActive(this.active <= 0 ? -1 : this.active - 1);
          break;
        case 'Enter':
          if (isOpen && this.active > -1) {
            event.preventDefault();
            this.choose(this.active);
          }
          break;
        case 'Escape':
          if (isOpen) {
            event.preventDefault();
            this.close();
          } else if (this.input.value) {
            event.preventDefault();
            this.input.value = '';
            this.query = '';
            this.results = [];
            this.toggleClear();
          }
          break;
        case 'Tab':
          this.close();
          break;
        default:
          break;
      }
    }

    choose(i) {
      const option = this.options[i];
      if (!option) return;
      const query = this.input.value.trim();
      const kind = option.className.match(/--(\w+)$/);
      if (query.length >= 2) {
        const top = this.results[0] ? this.results[0].doc.h : '';
        trackSearch(this.source, query, this.results.length, top, 'index');
      }
      if (kind && kind[1] === 'contact') {
        track('ht_help_contact_click', { from: 'search', source: this.source });
      } else if (option.dataset.handle) {
        track('ht_help_result_click', {
          q: redact(query),
          handle: option.dataset.handle,
          position: i + 1,
          source: this.source,
          kind: kind ? kind[1] : 'answer',
        });
      }
      window.location.href = option.dataset.href;
    }

    onSubmit(event) {
      const query = this.input.value.trim();
      window.clearTimeout(this.settleTimer);
      if (!query) {
        event.preventDefault();
        this.input.focus();
        return;
      }
      if (intent(query)) {
        // Order numbers, emails and phone numbers never go to /search or analytics as typed.
        event.preventDefault();
        const first = this.options.find((o) => o.dataset.handle);
        trackSearch(this.source, query, this.results.length, first ? first.dataset.handle : '', 'index');
        if (first) window.location.href = first.dataset.href;
        else if (this.config.contactUrl) window.location.href = this.config.contactUrl;
        return;
      }
      const top = this.results[0] ? this.results[0].doc.h : '';
      trackSearch(this.source, query, this.results.length, top, 'index');
    }
  }

  if (!customElements.get('ht-help-search')) customElements.define('ht-help-search', HtHelpSearch);

  /* Category pages: answers collapse into buttons, and #handle opens one ------------------------ */

  function initAccordions(root) {
    const items = Array.prototype.slice.call(root.querySelectorAll('.hthc-qa'));
    items.forEach((item) => {
      const button = item.querySelector('.hthc-qa__toggle');
      const panel = item.querySelector('.hthc-qa__answer');
      if (!button || !panel || item.dataset.ready) return;
      item.dataset.ready = 'true';
      button.hidden = false;
      const plain = item.querySelector('.hthc-qa__heading-text');
      if (plain) plain.hidden = true;
      panel.hidden = true;
      button.setAttribute('aria-expanded', 'false');
      button.addEventListener('click', () => {
        const open = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', open ? 'true' : 'false');
        panel.hidden = !open;
        if (open) {
          track('ht_help_answer_open', { handle: item.dataset.handle || '', source: 'category' });
          if (window.history && window.history.replaceState) {
            window.history.replaceState(null, '', '#' + item.id);
          }
        }
      });
    });
    let hash = window.location.hash.slice(1);
    try {
      hash = decodeURIComponent(hash);
    } catch (error) {
      // A malformed hash stays as typed.
    }
    const target = hash ? document.getElementById(hash) : null;
    if (target && target.classList.contains('hthc-qa')) {
      const button = target.querySelector('.hthc-qa__toggle');
      if (button && button.getAttribute('aria-expanded') !== 'true') button.click();
      window.setTimeout(() => target.scrollIntoView({ block: 'start' }), 60);
    }
  }

  /* "Was this helpful?" ------------------------------------------------------------------------- */

  function initFeedback(root) {
    root.querySelectorAll('.hthc-feedback').forEach((box) => {
      if (box.dataset.ready) return;
      box.dataset.ready = 'true';
      const buttons = box.querySelectorAll('[data-hthc-vote]');
      const thanks = box.querySelector('.hthc-feedback__thanks');
      const sorry = box.querySelector('.hthc-feedback__sorry');
      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          const vote = button.getAttribute('data-hthc-vote');
          buttons.forEach((b) => b.setAttribute('aria-pressed', b === button ? 'true' : 'false'));
          if (thanks) thanks.hidden = vote !== 'yes';
          if (sorry) sorry.hidden = vote !== 'no';
          if (box.dataset.voted === vote) return;
          box.dataset.voted = vote;
          track('ht_help_feedback', { handle: box.dataset.handle || '', helpful: vote });
        });
      });
    });
  }

  /* Contact links carry the answer or topic the customer came from ----------------------------- */

  function initContactLinks(root) {
    root.querySelectorAll('[data-hthc-contact]').forEach((link) => {
      if (link.dataset.ready) return;
      link.dataset.ready = 'true';
      link.addEventListener('click', () => {
        track('ht_help_contact_click', {
          from: link.dataset.from || '',
          source: link.dataset.source || '',
        });
      });
    });
  }

  /* Help search results page: closest answers when Shopify's search finds none ---------------- */

  function initResults(root) {
    root.querySelectorAll('[data-hthc-results]').forEach((box) => {
      if (box.dataset.ready) return;
      box.dataset.ready = 'true';
      const query = box.dataset.query || '';
      const native = parseInt(box.dataset.native || '0', 10) || 0;
      const config = readJSON(box.querySelector('script[data-hthc-config]')) || {};
      if (!query) return;
      if (intent(query)) return;
      // Later pages of the same search were already counted on page 1.
      if ((parseInt(box.dataset.page || '1', 10) || 1) > 1) return;
      loadIndex(box.dataset.indexUrl, config.synonyms || '')
        .then((index) => {
          const results = search(index, query, { limit: 6 });
          trackSearch('results', query, Math.max(results.length, native), results[0] ? results[0].doc.h : '', native ? 'native' : 'index');
          if (native || !results.length) return;
          const list = box.querySelector('.hthc-results__closest');
          const wrap = box.querySelector('.hthc-results__closest-wrap');
          if (!list || !wrap) return;
          results.forEach((r) => {
            const li = element('li', 'hthc-card-list__item');
            const a = element('a', 'hthc-card-list__link');
            a.href = r.doc.u;
            a.innerHTML =
              '<span class="hthc-card-list__q">' + highlight(index, r.doc.t, r.hits) + '</span>' +
              (r.doc.c ? '<span class="hthc-card-list__cat">' + escapeHtml(r.doc.c) + '</span>' : '');
            a.addEventListener('click', () =>
              track('ht_help_result_click', { q: redact(query), handle: r.doc.h, position: 0, source: 'results', kind: 'closest' })
            );
            li.appendChild(a);
            list.appendChild(li);
          });
          wrap.hidden = false;
        })
        .catch(() => {
          trackSearch('results', query, native, '', 'native');
        });
    });
  }

  /* Contact page: topic prompts, answer suggestions while typing, context in the email --------- */

  function initContact(root) {
    root.querySelectorAll('.hthc-contact').forEach((box) => {
      if (box.dataset.ready) return;
      box.dataset.ready = 'true';
      const config = readJSON(box.querySelector('script[data-hthc-config]')) || {};
      const topics = box.querySelectorAll('input[name="contact[Topic]"]');
      const message = box.querySelector('textarea');
      const suggest = box.querySelector('.hthc-contact__suggest');
      const suggestList = box.querySelector('.hthc-contact__suggest-list');
      const suggestTitle = box.querySelector('.hthc-contact__suggest-title');
      const suggestStatus = box.querySelector('[data-hthc-suggest-status]');
      const prompt = box.querySelector('.hthc-contact__prompt');
      const context = box.querySelector('[data-hthc-context-field]');
      const orderField = box.querySelector('input[name="contact[Order number]"]');
      const form = box.querySelector('form');
      const params = new URLSearchParams(window.location.search);
      const focusTarget = box.querySelector('[data-hthc-focus]');
      if (focusTarget) focusTarget.focus();
      let indexPromise = null;
      let shown = 0;
      let lastTopic = '';
      let typingTimer = null;

      function index() {
        if (!indexPromise) {
          indexPromise = loadIndex(box.dataset.indexUrl, config.synonyms || '').catch(() => {
            indexPromise = null;
            return null;
          });
        }
        return indexPromise;
      }

      function showSuggestions(title, items, why) {
        if (!suggest || !suggestList) return;
        suggestList.textContent = '';
        items.slice(0, 3).forEach((doc) => {
          const li = element('li', 'hthc-contact__suggest-item');
          const a = element('a', 'hthc-contact__suggest-link', doc.t);
          a.href = doc.u;
          a.addEventListener('click', () =>
            track('ht_help_deflection_click', { handle: doc.h, topic: lastTopic, why: why })
          );
          li.appendChild(a);
          suggestList.appendChild(li);
        });
        if (suggestTitle) suggestTitle.textContent = title;
        suggest.hidden = !items.length;
        if (items.length) shown += 1;
        // The suggestions box starts hidden, so announce through a status line that is always there.
        if (suggestStatus) {
          const count = Math.min(items.length, 3);
          suggestStatus.textContent = count
            ? title + ': ' + count + (count === 1 ? ' answer' : ' answers') + ' listed above the form.'
            : '';
        }
      }

      function setTopic(handle) {
        lastTopic = handle;
        const topic = (config.topics || []).find((t) => t.handle === handle);
        if (prompt) {
          prompt.textContent = topic && topic.prompt ? topic.prompt : '';
          prompt.hidden = !(topic && topic.prompt);
        }
        if (!topic) {
          if (suggest) suggest.hidden = true;
          return;
        }
        index().then((idx) => {
          if (!idx || lastTopic !== handle) return;
          const docs = idx.docs
            .map((d) => d.a)
            .filter((a) => a.ch === handle)
            .sort((a, b) => (a.p || 999) - (b.p || 999));
          showSuggestions(config.topicTitle || 'Quick answers for this', docs, 'topic');
        });
      }

      topics.forEach((radio) => {
        radio.addEventListener('change', () => {
          if (radio.checked) setTopic(radio.dataset.handle || '');
        });
      });
      // After a failed submit Shopify re-renders the page from the POST, so the topic and order
      // number come back from what was saved on submit; otherwise the topic comes from the link.
      const DRAFT_KEY = 'hthc:contact';
      let saved = null;
      if (box.querySelector('.hthc-contact__errors')) {
        try {
          saved = JSON.parse(recall(DRAFT_KEY) || 'null');
        } catch (error) {
          saved = null;
        }
      }
      const preset = (saved && saved.topic) || params.get('topic');
      if (preset) {
        const radio = Array.prototype.find.call(topics, (r) => r.dataset.handle === preset);
        if (radio) {
          radio.checked = true;
          setTopic(preset);
        }
      }
      if (saved && orderField && saved.order && !orderField.value) orderField.value = saved.order;

      if (message) {
        message.addEventListener('input', () => {
          window.clearTimeout(typingTimer);
          typingTimer = window.setTimeout(() => {
            const text = message.value.trim();
            if (text.split(/\s+/).length < 3) return;
            index().then((idx) => {
              if (!idx) return;
              const results = search(idx, text, { mode: 'long', limit: 3 });
              if (results.length) {
                showSuggestions(config.typingTitle || 'These might sort it', results.map((r) => r.doc), 'typing');
              }
            });
          }, ANNOUNCE_MS);
        });
      }

      if (context) {
        const parts = [];
        const from = params.get('from');
        if (from) parts.push('Help answer: ' + from.replace(/[^a-z0-9-]/gi, '').slice(0, 80));
        const lastQuery = recall(LAST_QUERY_KEY);
        if (lastQuery) parts.push('Last help search: ' + lastQuery);
        context.value = (saved && saved.context) || parts.join(' | ');
      }

      if (form) {
        form.addEventListener('submit', () => {
          remember(
            DRAFT_KEY,
            JSON.stringify({
              topic: lastTopic,
              order: orderField ? orderField.value.slice(0, 40) : '',
              context: context ? context.value : '',
            })
          );
          track('ht_help_contact_submit', {
            topic: lastTopic || 'none',
            from_help: params.get('from') ? 'yes' : 'no',
            suggestions_shown: shown,
          });
        });
      }
    });
  }

  /* Service notice: the page may be cached, so check its dates against the visitor's clock ----- */

  function initNotices(root) {
    const now = Date.now() / 1000;
    root.querySelectorAll('.hthc-notice[data-starts], .hthc-notice[data-ends]').forEach((notice) => {
      const starts = parseInt(notice.dataset.starts || '0', 10);
      const ends = parseInt(notice.dataset.ends || '0', 10);
      notice.hidden = Boolean((starts && now < starts) || (ends && now > ends));
    });
  }

  function init(root) {
    // Each part runs on its own, so a failure in one can't stop the rest.
    [initNotices, initAccordions, initFeedback, initContactLinks, initResults, initContact].forEach((fn) => {
      try {
        fn(root);
      } catch (error) {
        if (window.console) window.console.error('Help centre:', error);
      }
    });
  }

  function boot() {
    init(document);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
